import React, { useState } from 'react';
import type { StarRatingQuestion as StarRatingQuestionType } from './survey.types';
import { useLanguageStore } from '../../stores/languageStore';

interface StarRatingQuestionProps {
  question: StarRatingQuestionType;
  value: number | null;
  onChange: (value: number) => void;
  error?: string;
  displayNumber?: number;
}

export const StarRatingQuestion: React.FC<StarRatingQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  displayNumber,
}) => {
  const languageCode = useLanguageStore((state) => state.code);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  // Use maxSelectable if provided, otherwise default to 5 stars
  const numberOfStars = question.maxSelectable ?? 5;
  const activeCount = hoveredStar ?? (value || 0);

  const handleStarClick = (star: number) => {
    // Clear hover state on click to prevent mobile touch issues
    setHoveredStar(null);
    onChange(star);
  };

  const FullStar = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 700 700"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="m279.82 210h-209.75l174.63 138-69.738 211.93 175.26-130.69 174.72 130.69-69.457-213.78 174.65-136.15h-209.77l-70.402-209.98z"></path>
    </svg>
  );

  const EmptyStar = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 700 700"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="m350.04 136.11 44.504 108.89h130.41l-111.34 84.98 43.328 136.08-106.7-81.129-106.7 79.223 43.191-131.93-111.73-87.219h130.69l44.344-108.89m-70.191 73.887h-209.77l174.63 138.04-69.703 211.95 175.25-130.67 174.7 130.69-69.457-213.82 174.65-136.18h-209.77l-70.402-209.98z"></path>
    </svg>
  );

  const groupLabel =
    languageCode === 'en' ? 'Star rating' : 'Évaluation par étoiles';

  const rateLabelPrefix = languageCode === 'en' ? 'Rate' : 'Noter';

  return (
    <div className="survey-question star-rating-question">
      <label className="question-label">
        {displayNumber != null && (
          <span className="question-number">{displayNumber}.</span>
        )}
        <span dangerouslySetInnerHTML={{ __html: question.question_text[languageCode] }} />
        {question.is_required && <span className="required-indicator">*</span>}
      </label>
      <div className="d-flex flex-column star-rating-block">
        <div className="d-flex starsContainer">
          <span className="stars" role="radiogroup" aria-label={groupLabel}>
            {Array.from({ length: numberOfStars }, (_, i) => i + 1).map((star) => {
              const isActive = star <= activeCount;
              return (
                <span key={star} className="star-wrapper" data-rating={star}>
                  <button
                    type="button"
                    className={`star ${isActive ? 'full' : 'empty'}`}
                    data-rating={star}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(null)}
                    onTouchStart={() => setHoveredStar(star)}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      handleStarClick(star);
                    }}
                    onClick={() => handleStarClick(star)}
                    role="radio"
                    aria-checked={value === star}
                    aria-label={`${rateLabelPrefix} ${star}`}
                  >
                    {isActive ? <FullStar /> : <EmptyStar />}
                  </button>
                </span>
              );
            })}
          </span>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

